/**
 * Marketing Dashboard (V2) — restructured per the new spec.
 *
 * Sections (in order, top to bottom on the page):
 *   1. Global filter bar (date range, campaign, platform, channel,
 *      project, lead status). Filters apply to every widget below
 *      AND persist across navigation via URL search params.
 *   2. Executive Summary — top KPI strip: total leads, reach,
 *      engagement, spend, ROAS, conversion rate.
 *   3. Lead Bucket tiles — six visual tiles (Hot / Warm / Cold /
 *      Follow-up / Closed Won / Lost) with delta arrows.
 *   4. AI Insight cards — dismissible cards woven into the layout.
 *      Visible to marketing + leadership only.
 *   5. Awareness section — paid + organic KPIs side-by-side. Cost
 *      KPIs are stripped for organic per business rule.
 *   6. Performance / Funnel — 8-stage funnel with drop-off %.
 *   7. Platform comparison — bar chart per platform.
 *   8. Campaign comparison — top + bottom campaigns table.
 *   9. Source attribution — pie/list breakdown.
 *
 * Filters live in URL search params so refreshing the page preserves
 * the marketer's view, and so deep-links from AI insight cards (e.g.
 * "Open campaign X") can pre-filter the dashboard.
 *
 * Removed (per spec): Live Agent Tracker, Site Visit Tracking — both
 * confirmed absent from the previous Marketing Dashboard page; this
 * V2 rebuild leaves them out explicitly.
 *
 * Permissions: marketing / leadership see everything including AI +
 * spend. Sales agents read-only with cost KPIs hidden (the backend
 * already strips them; the UI hides the AI insights section).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Flame, Cloud, Snowflake, Bell, CheckCircle2, XCircle,
  TrendingUp, TrendingDown, Minus, BarChart3, Filter, Download,
  RefreshCw, Sparkles, Eye, Target, DollarSign, Users, Activity,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import MarketingLeadsOverview from '@/components/MarketingLeadsOverview';
import InfoBadge from '@/components/InfoBadge';
import { SourceAnalyticsTab, CampaignAlertsAndActive } from '@/pages/MyMarketingDashboard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORM_LABELS = {
  meta: 'Meta Ads',
  google: 'Google Ads',
  tiktok: 'TikTok Ads',
  youtube: 'YouTube',
  facebook_organic: 'Facebook (Organic)',
  instagram_organic: 'Instagram (Organic)',
  website: 'Website',
  referral: 'Referral',
  walk_in: 'Walk-in',
  other: 'Other',
};

const isMarketingOrLeader = (role) => {
  const r = (role || '').toLowerCase();
  return ['admin', 'owner', 'ceo', 'hod', 'marketing', 'super', 'indirect_sales_lead']
    .some((h) => r.includes(h));
};

const fmtNumber = (n) => {
  if (n == null) return '—';
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v.toLocaleString();
};

const fmtAED = (n) => {
  if (n == null) return '—';
  return `AED ${fmtNumber(n)}`;
};

const fmtPct = (n) => {
  if (n == null) return '—';
  return `${Number(n).toFixed(1)}%`;
};

export default function MarketingDashboardV2({
  embedded = false,
  // Scope props forwarded by the HOD dashboard's dark control bar.
  // Currently informational — surfaced here so future iterations
  // can re-key the data fetches to the chosen scope without changing
  // the call-site. Defaults mirror the standalone /dashboard route's
  // implicit "organization + agents" perspective.
  dashboardScope = 'organization',
  sliceBy = 'agents',
  withTeam = false,
} = {}) {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fullAccess = isMarketingOrLeader(user?.role);

  // ---- Filters synced to URL ----
  const filters = useMemo(() => {
    const obj = {};
    for (const [k, v] of searchParams.entries()) {
      if (v) obj[k] = v;
    }
    // Sensible defaults
    if (!obj.days && !obj.date_from && !obj.date_to) obj.days = '30';
    return obj;
  }, [searchParams]);

  const updateFilter = useCallback((key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value === null || value === undefined || value === '' || value === 'all') {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const clearFilters = () => setSearchParams({}, { replace: true });

  // ---- Data fetching ----
  const [summary, setSummary] = useState(null);
  const [buckets, setBuckets] = useState(null);
  const [funnel, setFunnel] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [campaigns, setCampaigns] = useState({ top: [], bottom: [] });
  const [sources, setSources] = useState([]);
  const [paidKpis, setPaidKpis] = useState([]);
  const [organicKpis, setOrganicKpis] = useState([]);
  const [insights, setInsights] = useState([]);
  const [trend, setTrend] = useState({ days: [] });
  const [loading, setLoading] = useState(true);
  const [refreshingInsights, setRefreshingInsights] = useState(false);
  // The AI Insights panel used to sit inline between the bucket
  // tiles and the tab deep-dive, taking up ~200-400px of vertical
  // real estate on every page load. Moved into a Dialog opened from
  // the header — the button sits beside Refresh / Export CSV. Same
  // component, same data, just off the main scroll path.
  const [isInsightsOpen, setIsInsightsOpen] = useState(false);

  const queryFromFilters = useCallback(() => {
    const q = { token };
    for (const [k, v] of Object.entries(filters)) {
      if (k === 'days') continue; // we translate days → date_from/date_to below
      q[k] = v;
    }
    if (filters.days && !filters.date_from && !filters.date_to) {
      const today = new Date();
      const from = new Date(today.getTime() - (Number(filters.days) || 30) * 86400000);
      q.date_from = from.toISOString();
      q.date_to = today.toISOString();
    }
    return q;
  }, [token, filters]);

  const loadAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    const params = queryFromFilters();

    // Progressive fetch — each request is independent, so we drop
    // the loading spinner as soon as the FIRST one lands (or after a
    // 2s safety timeout) instead of waiting on Promise.allSettled.
    // Slow endpoints (awareness/organic, insights) used to gate the
    // entire shell behind the slowest call, leaving the whole page
    // as gray skeletons for several seconds even when the fast
    // endpoints had already returned.
    let firstDone = false;
    const paint = () => {
      if (firstDone) return;
      firstDone = true;
      setLoading(false);
    };
    const safety = setTimeout(paint, 2000);
    const track = (p) => p.finally(paint);

    const calls = [
      track(axios.get(`${API}/marketing-dashboard/executive-summary`, { params })
        .then((r) => setSummary(r.data?.data || null))
        .catch((e) => console.warn('summary:', e?.response?.data?.detail || e))),
      track(axios.get(`${API}/marketing-dashboard/lead-buckets`, { params })
        .then((r) => setBuckets(r.data?.data || null))
        .catch((e) => console.warn('buckets:', e))),
      track(axios.get(`${API}/marketing-dashboard/performance/funnel`, { params })
        .then((r) => setFunnel(r.data?.data?.funnel || []))
        .catch((e) => console.warn('funnel:', e))),
      track(axios.get(`${API}/marketing-dashboard/platforms`, { params })
        .then((r) => setPlatforms(r.data?.data?.platforms || []))
        .catch((e) => console.warn('platforms:', e))),
      track(axios.get(`${API}/marketing-dashboard/campaigns`, { params: { ...params, sort_by: 'leads', limit: 5 } })
        .then((r) => setCampaigns(r.data?.data || { top: [], bottom: [] }))
        .catch((e) => console.warn('campaigns:', e))),
      track(axios.get(`${API}/marketing-dashboard/sources`, { params })
        .then((r) => setSources(r.data?.data?.sources || []))
        .catch((e) => console.warn('sources:', e))),
      track(axios.get(`${API}/marketing-dashboard/performance/conversion-trend`, { params })
        .then((r) => setTrend(r.data?.data || { days: [] }))
        .catch((e) => console.warn('trend:', e))),
    ];
    if (fullAccess) {
      calls.push(
        track(axios.get(`${API}/marketing-dashboard/awareness/paid`, { params })
          .then((r) => setPaidKpis(r.data?.data?.campaigns || []))
          .catch((e) => console.warn('paid:', e))),
        track(axios.get(`${API}/marketing-dashboard/awareness/organic`, { params })
          .then((r) => setOrganicKpis(r.data?.data?.posts || []))
          .catch((e) => console.warn('organic:', e))),
      );
      // Insights are behind the AI Insights modal button — fetch
      // lazily when the user opens the modal (see the effect below),
      // not on initial page load. That drops the slowest call off
      // the critical path.
    }

    // Clear the safety timer once everything genuinely finished so
    // a re-render on unmount doesn't ghost-write state.
    Promise.allSettled(calls).finally(() => clearTimeout(safety));
  }, [token, queryFromFilters, fullAccess]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Lazy-load AI insights only when the modal opens. Skips the
  // heavy LLM-adjacent endpoint from the initial-load critical path.
  // Idempotent — once insights are populated we don't refetch until
  // the user hits the Refresh button inside the modal.
  useEffect(() => {
    if (!isInsightsOpen || !token || !fullAccess) return;
    if (insights.length > 0) return;
    axios
      .get(`${API}/marketing-dashboard/insights`, { params: { token } })
      .then((r) => setInsights(r.data?.data?.insights || []))
      .catch((e) => console.warn('insights:', e));
  }, [isInsightsOpen, token, fullAccess, insights.length]);

  // ---- AI insights actions ----
  const dismissInsight = async (id) => {
    try {
      await axios.post(`${API}/marketing-dashboard/insights/${id}/dismiss?token=${token}`);
      setInsights((prev) => prev.filter((i) => i.id !== id));
      toast.success('Insight dismissed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not dismiss');
    }
  };

  const refreshInsights = async () => {
    setRefreshingInsights(true);
    try {
      await axios.post(`${API}/marketing-dashboard/insights/refresh?token=${token}`);
      const r = await axios.get(`${API}/marketing-dashboard/insights`, { params: { token } });
      setInsights(r.data?.data?.insights || []);
      toast.success('Insights refreshed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Refresh failed');
    } finally {
      setRefreshingInsights(false);
    }
  };

  const exportCsv = async () => {
    try {
      const params = queryFromFilters();
      const r = await axios.get(`${API}/marketing-dashboard/export/csv`, {
        params, responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `marketing_leads_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Exported CSV');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export failed');
    }
  };

  // When embedded inside another dashboard page (e.g. HOD Marketing),
  // the host already supplies the page chrome (background, max-width,
  // outer padding). Wrapping again in min-h-screen + bg + max-w + p-6
  // produces a huge dead gap under the last panel and double-pads the
  // grid. The `embedded` prop swaps the wrapper for a thin pass-through
  // so the dashboard sits flush inside its host.
  const outerClasses = embedded
    ? ''
    : 'min-h-screen bg-slate-50 dark:bg-slate-950';
  const innerClasses = embedded
    ? 'space-y-7'
    : 'max-w-[1600px] mx-auto px-6 py-8 space-y-7';

  return (
    <div className={outerClasses}>
      <div className={innerClasses}>
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Sparkles size={26} className="text-violet-500" />
              Marketing Dashboard
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Awareness · performance · funnel · AI insights — across every connected platform.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {fullAccess && (
              <Button variant="outline" size="sm" onClick={exportCsv}>
                <Download size={14} className="mr-1.5" /> Export CSV
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={loadAll} disabled={loading}>
              <RefreshCw size={14} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            {/* AI Insights — opens the same panel that used to sit
                inline, now as a floating modal. Badge shows the live
                insight count so the button doubles as a signal. */}
            {fullAccess && (
              <Button
                size="sm"
                onClick={() => setIsInsightsOpen(true)}
                className="bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="ai-insights-btn"
              >
                <Sparkles size={14} className="mr-1.5" />
                AI Insights
                {insights.length > 0 && (
                  <Badge className="ml-2 bg-white/25 text-white border-0 px-1.5 py-0 h-4 text-[10px]">
                    {insights.length}
                  </Badge>
                )}
              </Button>
            )}
          </div>
        </div>

        {/* ---- Marketing Leads Overview ----
            The "Welcome back" greeting + Total Leads tile + tab filter +
            8 status pills, plus the Leads vs Source donut and 5-stage
            Lead Funnel. Sits ABOVE the rest of the dashboard so a
            marketing agent gets the lead picture before the deeper
            campaign / awareness / funnel tabs. */}
        <MarketingLeadsOverview />

        {/* ---- Global filter bar ---- */}
        <FilterBar
          filters={filters}
          onChange={updateFilter}
          onClear={clearFilters}
        />

        {/* ---- Executive Summary ---- */}
        <ExecSummary summary={summary} loading={loading} fullAccess={fullAccess} />

        {/* ---- Lead bucket tiles ---- */}
        <LeadBucketTiles buckets={buckets} loading={loading} onTileClick={(key) => {
          // Tile click → drill into leads with that temperature/status
          const tempMap = { hot: 'hot', warm: 'warm', cold: 'cold', dropped: 'dropped' };
          if (tempMap[key]) updateFilter('temperature', tempMap[key]);
          else if (key === 'closed_won') {
            updateFilter('stage', 'closed');
          } else if (key === 'lost') {
            updateFilter('stage', 'closed');
          }
        }} />

        {/* AI Insights moved into the floating dialog opened from the
            "AI Insights" button in the header — see near the bottom of
            this component. Keeps the main page focused on the funnel
            and tab drill-downs without losing access to the AI notes. */}

        {/* ---- Tabbed deep-dive (Awareness / Performance / Sources) ---- */}
        <Tabs defaultValue="awareness" className="pt-2">
          <TabsList className="h-11 mb-4">
            <TabsTrigger value="awareness" className="px-4"><Eye size={14} className="mr-1.5" /> Awareness</TabsTrigger>
            <TabsTrigger value="performance" className="px-4"><Target size={14} className="mr-1.5" /> Performance</TabsTrigger>
            <TabsTrigger value="funnel" className="px-4"><BarChart3 size={14} className="mr-1.5" /> Funnel</TabsTrigger>
            <TabsTrigger value="campaigns" className="px-4"><Activity size={14} className="mr-1.5" /> Campaigns</TabsTrigger>
            <TabsTrigger value="sources" className="px-4"><Users size={14} className="mr-1.5" /> Sources</TabsTrigger>
          </TabsList>

          <TabsContent value="awareness" className="space-y-4">
            <AwarenessSection
              summary={summary}
              paidKpis={paidKpis}
              organicKpis={organicKpis}
              platforms={platforms}
              fullAccess={fullAccess}
              loading={loading}
            />
          </TabsContent>

          <TabsContent value="performance" className="space-y-4">
            <PerformanceSection
              summary={summary}
              trend={trend}
              fullAccess={fullAccess}
              loading={loading}
              token={token}
            />
          </TabsContent>

          <TabsContent value="funnel" className="space-y-4">
            <FunnelSection funnel={funnel} loading={loading} />
          </TabsContent>

          <TabsContent value="campaigns" className="space-y-4">
            <CampaignsSection
              campaigns={campaigns}
              platforms={platforms}
              fullAccess={fullAccess}
              loading={loading}
              token={token}
            />
          </TabsContent>

          <TabsContent value="sources" className="space-y-4">
            <SourcesSection sources={sources} loading={loading} />
          </TabsContent>
        </Tabs>
      </div>

      {/* AI Insights dialog — opens from the header button. Reuses
          the same AIInsightsSection so behaviour matches the old
          inline card 1:1: dismiss, refresh, "Why this insight?"
          expand all still work. Wrapping the section in DialogContent
          gives it a scrollable capsule so long insight lists don't
          push the modal off-screen. */}
      <Dialog open={isInsightsOpen} onOpenChange={setIsInsightsOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b bg-gradient-to-r from-violet-50 to-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center shrink-0">
                <Sparkles size={20} />
              </div>
              <div className="flex-1">
                <DialogTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  AI Insights
                  {insights.length > 0 && (
                    <Badge variant="outline" className="text-[10px]">{insights.length}</Badge>
                  )}
                </DialogTitle>
                <DialogDescription className="text-xs text-slate-500 mt-0.5">
                  Refreshed daily · click "Why this insight?" on any card to see the underlying data.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="overflow-y-auto p-6 flex-1">
            <AIInsightsSection
              insights={insights}
              onDismiss={dismissInsight}
              onRefresh={refreshInsights}
              refreshing={refreshingInsights}
              embedded
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================
// Filter Bar
// ============================================================
const FilterBar = ({ filters, onChange, onClear }) => {
  const days = filters.days || '30';
  return (
    <Card>
      <CardContent className="px-4 py-3.5 flex items-center gap-2.5 flex-wrap">
        <Filter size={14} className="text-slate-400" />
        <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold mr-1">Filters</span>

        <Select value={days} onValueChange={(v) => onChange('days', v)}>
          <SelectTrigger className="h-9 w-[150px]"><SelectValue placeholder="Date range" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">Today</SelectItem>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="365">Last year</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filters.platform || 'all'} onValueChange={(v) => onChange('platform', v)}>
          <SelectTrigger className="h-9 w-[150px]"><SelectValue placeholder="Platform" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All platforms</SelectItem>
            {Object.entries(PLATFORM_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          className="h-9 w-[200px]"
          placeholder="Campaign id / name"
          value={filters.campaign || ''}
          onChange={(e) => onChange('campaign', e.target.value)}
        />

        <Input
          className="h-9 w-[170px]"
          placeholder="Source"
          value={filters.lead_source || ''}
          onChange={(e) => onChange('lead_source', e.target.value)}
        />

        <Input
          className="h-9 w-[150px]"
          placeholder="Project"
          value={filters.project || ''}
          onChange={(e) => onChange('project', e.target.value)}
        />

        <Select value={filters.temperature || 'all'} onValueChange={(v) => onChange('temperature', v)}>
          <SelectTrigger className="h-9 w-[130px]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="hot">🔥 Hot</SelectItem>
            <SelectItem value="warm">🌤️ Warm</SelectItem>
            <SelectItem value="cold">❄️ Cold</SelectItem>
            <SelectItem value="dropped">⛔ Dropped</SelectItem>
          </SelectContent>
        </Select>

        {Object.keys(filters).length > 0 && (
          <Button variant="ghost" size="sm" className="h-9 text-xs ml-auto" onClick={onClear}>
            <X size={12} className="mr-1" /> Clear all
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

// ============================================================
// Executive Summary tiles
// ============================================================
const ExecSummary = ({ summary, loading, fullAccess }) => {
  if (loading && !summary) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-8 gap-4">
        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => <Skeleton key={i} className="h-28" />)}
      </div>
    );
  }
  if (!summary) return null;
  const aw = summary.awareness || {};
  const pf = summary.performance || {};

  const tiles = [
    { label: 'Total Leads', value: fmtNumber(pf.total_leads), icon: Users, tone: 'violet',
      info: 'Every contact captured in the selected window across every source. Includes unqualified and disqualified.' },
    { label: 'Qualified', value: fmtNumber(pf.qualified), icon: CheckCircle2, tone: 'blue',
      info: 'Leads that progressed past qualification (lifecycle_stage in qualified / site_visit / negotiation / booking / closed).' },
    { label: 'Closed Sales', value: fmtNumber(pf.closed_sales), icon: Target, tone: 'emerald',
      info: 'Leads with closed_outcome = won. The actual deal count this period — basis for revenue.' },
    { label: 'Total Reach', value: fmtNumber(aw.total_reach), icon: Eye, tone: 'amber',
      info: 'Unique people who saw any owned post / ad across connected social platforms in the window.' },
    { label: 'CTR', value: fmtPct(aw.ctr_pct), icon: Activity, tone: 'blue',
      info: 'Click-through rate = Clicks ÷ Impressions × 100. Industry benchmark is 1–2% for paid social.' },
    ...(fullAccess ? [
      { label: 'Spend', value: fmtAED(pf.spend), icon: DollarSign, tone: 'rose',
        info: 'Total media spend across all campaigns in the window, in AED. Used as the denominator for CPL, CAC, and ROAS.' },
      { label: 'ROAS', value: pf.roas ? `${pf.roas}x` : '—', icon: TrendingUp, tone: 'emerald',
        info: 'Return on Ad Spend = Revenue ÷ Spend. 1× breaks even; 3× is the rule-of-thumb target for paid marketing.' },
      { label: 'CPL', value: fmtAED(pf.cpl), icon: DollarSign, tone: 'amber',
        info: 'Cost per Lead = Spend ÷ Total Leads. Compare against the lead-to-won rate to see if cheap leads are actually closing.' },
    ] : []),
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-8 gap-4">
      {tiles.map((t) => <KpiTile key={t.label} {...t} />)}
    </div>
  );
};

const TONE_STYLES = {
  violet:  'border-violet-200 bg-violet-50 text-violet-800',
  blue:    'border-blue-200 bg-blue-50 text-blue-800',
  emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  amber:   'border-amber-200 bg-amber-50 text-amber-800',
  rose:    'border-rose-200 bg-rose-50 text-rose-800',
  slate:   'border-slate-200 bg-slate-50 text-slate-800',
};
const KpiTile = ({ label, value, icon: Icon, tone, info }) => (
  <div className={`rounded-xl border p-4 ${TONE_STYLES[tone] || TONE_STYLES.slate}`}>
    <div className="flex items-center justify-between gap-2">
      <span className="text-[10.5px] uppercase font-semibold tracking-wider flex-1 truncate">
        {label}
      </span>
      <InfoBadge text={info} />
      <Icon size={15} className="opacity-60 shrink-0" />
    </div>
    <div className="text-2xl font-bold mt-2 leading-none tabular-nums">{value ?? '—'}</div>
  </div>
);

// ============================================================
// Lead Bucket tiles
// ============================================================
const BUCKET_DEFS = [
  { key: 'hot',         label: 'Hot Leads',      icon: Flame,        tone: 'rose'    },
  { key: 'warm',        label: 'Warm Leads',     icon: Cloud,        tone: 'amber'   },
  { key: 'cold',        label: 'Cold Leads',     icon: Snowflake,    tone: 'blue'    },
  { key: 'follow_up',   label: 'Follow-up',      icon: Bell,         tone: 'violet'  },
  { key: 'closed_won',  label: 'Closed Deals',   icon: CheckCircle2, tone: 'emerald' },
  { key: 'lost',        label: 'Lost Leads',     icon: XCircle,      tone: 'slate'   },
];

const LeadBucketTiles = ({ buckets, loading, onTileClick }) => {
  if (loading && !buckets) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {BUCKET_DEFS.map((b) => <Skeleton key={b.key} className="h-32" />)}
      </div>
    );
  }
  if (!buckets) return null;
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
      {BUCKET_DEFS.map((b) => {
        const data = buckets[b.key] || {};
        const delta = data.delta || {};
        const Icon = b.icon;
        const TrendIcon =
          delta.direction === 'up' ? TrendingUp :
          delta.direction === 'down' ? TrendingDown : Minus;
        return (
          <button
            type="button"
            key={b.key}
            onClick={() => onTileClick?.(b.key)}
            className={`text-left rounded-xl border p-4 transition hover:shadow-md hover:-translate-y-0.5 ${TONE_STYLES[b.tone]}`}
          >
            <div className="flex items-center justify-between">
              <span className="text-[10.5px] uppercase font-semibold tracking-wider flex items-center gap-1.5">
                <Icon size={12} /> {b.label}
              </span>
            </div>
            <div className="text-3xl font-bold mt-2.5 leading-none tabular-nums">
              {fmtNumber(data.count)}
            </div>
            <div className="mt-3 flex items-center gap-1 text-[11.5px] opacity-80">
              <TrendIcon size={12} />
              <span>
                {delta.pct == null ? 'no prior' :
                  delta.pct === 0 ? 'flat vs last' :
                  `${delta.pct > 0 ? '+' : ''}${delta.pct}% vs last`}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
};

// ============================================================
// AI Insights section
// ============================================================
const CONF_TONE = {
  High:   'border-emerald-300 text-emerald-700 bg-emerald-50',
  Medium: 'border-amber-300 text-amber-700 bg-amber-50',
  Low:    'border-slate-300 text-slate-600 bg-slate-50',
};

const AIInsightsSection = ({ insights, onDismiss, onRefresh, refreshing, embedded }) => {
  const [expanded, setExpanded] = useState(null);

  // When embedded (rendered inside the AI Insights dialog), skip the
  // outer Card + header/description — the dialog already provides all
  // three. Keeps the empty-state and grid identical either way.
  const EmptyState = (
    <div className="py-6 text-sm text-slate-500 flex items-center justify-between gap-3 flex-wrap">
      <span>No insights ready yet. They refresh daily.</span>
      <Button variant="outline" size="sm" onClick={onRefresh} disabled={refreshing}>
        <RefreshCw size={13} className={`mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Generate now
      </Button>
    </div>
  );

  if (!insights || insights.length === 0) {
    if (embedded) return EmptyState;
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles size={16} className="text-violet-500" /> AI Insights
          </CardTitle>
        </CardHeader>
        <CardContent>{EmptyState}</CardContent>
      </Card>
    );
  }

  const grid = (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {insights.map((ins) => (
          <div key={ins.id} className="border border-slate-200 dark:border-slate-700 rounded-md p-3 bg-white dark:bg-slate-900">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap mb-1">
                  <Badge variant="outline" className="text-[10px]">
                    {ins.category?.replace(/_/g, ' ')}
                  </Badge>
                  <Badge className={`text-[10px] border ${CONF_TONE[ins.confidence] || CONF_TONE.Low}`}>
                    {ins.confidence}
                  </Badge>
                </div>
                <div className="font-semibold text-sm">{ins.title}</div>
                <p className="text-xs text-slate-600 dark:text-slate-300 mt-1">{ins.body}</p>
              </div>
              <button
                type="button"
                onClick={() => onDismiss(ins.id)}
                className="text-slate-400 hover:text-slate-700"
                title="Dismiss"
              >
                <X size={14} />
              </button>
            </div>
            <div className="mt-2 flex items-center gap-2">
              {ins.action_link && (
                <a
                  href={ins.action_link}
                  className="text-xs text-violet-600 hover:underline font-medium"
                >
                  {ins.action || 'Open'} →
                </a>
              )}
              <button
                type="button"
                onClick={() => setExpanded(expanded === ins.id ? null : ins.id)}
                className="text-xs text-slate-500 hover:text-slate-700"
              >
                {expanded === ins.id ? 'Hide' : 'Why this insight?'}
              </button>
            </div>
            {expanded === ins.id && ins.data && (
              <pre className="mt-2 text-[10px] text-slate-500 bg-slate-50 dark:bg-slate-800 rounded p-2 overflow-auto max-h-36">
                {JSON.stringify(ins.data, null, 2)}
              </pre>
            )}
          </div>
        ))}
    </div>
  );

  if (embedded) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-end">
          <Button variant="outline" size="sm" onClick={onRefresh} disabled={refreshing}>
            <RefreshCw size={13} className={`mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
        {grid}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles size={16} className="text-violet-500" /> AI Insights
            <Badge variant="outline" className="text-[10px]">{insights.length}</Badge>
          </CardTitle>
          <CardDescription>
            Refreshed daily · click "Why this insight?" to see the underlying data
          </CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={refreshing}>
          <RefreshCw size={13} className={`mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </CardHeader>
      <CardContent>{grid}</CardContent>
    </Card>
  );
};

// ============================================================
// Awareness section
// ============================================================
const AwarenessSection = ({ summary, paidKpis, organicKpis, platforms, fullAccess, loading }) => {
  if (!summary) return loading ? <Skeleton className="h-48" /> : null;
  const aw = summary.awareness || {};
  return (
    <>
      {/* Awareness KPI grid */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Awareness KPIs</CardTitle>
          <CardDescription>Aggregated across every connected platform.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
          <KpiTile label="Total Reach" value={fmtNumber(aw.total_reach)} icon={Eye} tone="violet" />
          <KpiTile label="Impressions" value={fmtNumber(aw.total_impressions)} icon={Eye} tone="blue" />
          <KpiTile label="Engagement" value={fmtNumber(aw.total_engagement)} icon={Activity} tone="emerald" />
          <KpiTile label="Engagement %" value={fmtPct(aw.engagement_rate)} icon={Activity} tone="emerald" />
          <KpiTile label="Video Views" value={fmtNumber(aw.total_video_views)} icon={Eye} tone="blue" />
          <KpiTile label="Followers ↑" value={fmtNumber(aw.total_followers_gained)} icon={TrendingUp} tone="violet" />
          <KpiTile label="Clicks" value={fmtNumber(aw.total_clicks)} icon={Target} tone="blue" />
          <KpiTile label="CTR" value={fmtPct(aw.ctr_pct)} icon={Activity} tone="amber" />
          {fullAccess && <KpiTile label="Spend" value={fmtAED(aw.total_spend)} icon={DollarSign} tone="rose" />}
          {fullAccess && <KpiTile label="CPM" value={fmtAED(aw.cpm)} icon={DollarSign} tone="amber" />}
        </CardContent>
        {!aw.synced && (
          <CardContent className="pt-0">
            <p className="text-xs text-amber-700">
              ℹ️ Platform sync hasn't populated marketing_platform_metrics yet — awareness numbers will reflect synced ads data once connected.
            </p>
          </CardContent>
        )}
      </Card>

      {/* Platform comparison */}
      {platforms.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Platform comparison</CardTitle>
            <CardDescription>Conversion + spend per channel.</CardDescription>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 border-b">
                <tr>
                  <th className="text-left py-2 px-2">Platform</th>
                  <th className="text-right py-2 px-2">Reach</th>
                  <th className="text-right py-2 px-2">Impr</th>
                  <th className="text-right py-2 px-2">Clicks</th>
                  <th className="text-right py-2 px-2">CTR</th>
                  <th className="text-right py-2 px-2">Leads</th>
                  <th className="text-right py-2 px-2">Closed</th>
                  <th className="text-right py-2 px-2">Conv %</th>
                  {fullAccess && <th className="text-right py-2 px-2">Spend</th>}
                  {fullAccess && <th className="text-right py-2 px-2">CPL</th>}
                </tr>
              </thead>
              <tbody>
                {platforms.map((p) => (
                  <tr key={p.platform} className="border-b border-slate-100 dark:border-slate-800">
                    <td className="py-2 px-2 font-medium">
                      {p.label}
                      {p.is_organic && <Badge variant="outline" className="ml-1 text-[10px] border-emerald-300 text-emerald-700">Organic</Badge>}
                    </td>
                    <td className="text-right py-2 px-2 font-mono">{fmtNumber(p.reach)}</td>
                    <td className="text-right py-2 px-2 font-mono">{fmtNumber(p.impressions)}</td>
                    <td className="text-right py-2 px-2 font-mono">{fmtNumber(p.clicks)}</td>
                    <td className="text-right py-2 px-2 font-mono">{fmtPct(p.ctr_pct)}</td>
                    <td className="text-right py-2 px-2 font-mono">{fmtNumber(p.leads)}</td>
                    <td className="text-right py-2 px-2 font-mono">{fmtNumber(p.closed_won)}</td>
                    <td className="text-right py-2 px-2 font-mono">{fmtPct(p.conv_pct)}</td>
                    {fullAccess && <td className="text-right py-2 px-2 font-mono">{p.is_organic ? '—' : fmtAED(p.spend)}</td>}
                    {fullAccess && <td className="text-right py-2 px-2 font-mono">{p.is_organic ? '—' : fmtAED(p.cpl)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Paid vs Organic side-by-side */}
      {fullAccess && (paidKpis.length > 0 || organicKpis.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Paid Campaigns</CardTitle>
              <CardDescription>Per-campaign reach + spend + engagement.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-1.5 max-h-96 overflow-y-auto">
              {paidKpis.length === 0 ? (
                <p className="text-xs text-slate-500">No paid campaigns in this window.</p>
              ) : paidKpis.map((c) => (
                <div key={(c.campaign_id || '') + (c.campaign_name || '')} className="text-xs border border-slate-200 rounded p-2">
                  <div className="font-semibold flex items-center justify-between">
                    <span>{c.campaign_name}</span>
                    <Badge variant="outline" className="text-[10px]">{PLATFORM_LABELS[c.platform] || c.platform}</Badge>
                  </div>
                  <div className="mt-1 grid grid-cols-3 gap-1 text-[11px] text-slate-600">
                    <span>Reach {fmtNumber(c.reach)}</span>
                    <span>Impr {fmtNumber(c.impressions)}</span>
                    <span>CTR {fmtPct(c.ctr_pct)}</span>
                    <span>Spend {fmtAED(c.spend)}</span>
                    <span>CPM {fmtAED(c.cpm)}</span>
                    <span>Engmt {fmtNumber(c.engagement)}</span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Organic Posts</CardTitle>
              <CardDescription>Engagement-driven (no cost KPIs).</CardDescription>
            </CardHeader>
            <CardContent className="space-y-1.5 max-h-96 overflow-y-auto">
              {organicKpis.length === 0 ? (
                <p className="text-xs text-slate-500">No organic posts in this window.</p>
              ) : organicKpis.map((c) => (
                <div key={(c.campaign_id || '') + (c.campaign_name || '')} className="text-xs border border-slate-200 rounded p-2">
                  <div className="font-semibold flex items-center justify-between">
                    <span>{c.campaign_name}</span>
                    <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700">{PLATFORM_LABELS[c.platform] || c.platform}</Badge>
                  </div>
                  <div className="mt-1 grid grid-cols-3 gap-1 text-[11px] text-slate-600">
                    <span>Reach {fmtNumber(c.reach)}</span>
                    <span>Likes {fmtNumber(c.likes)}</span>
                    <span>Shares {fmtNumber(c.shares)}</span>
                    <span>Comments {fmtNumber(c.comments)}</span>
                    <span>Engmt % {fmtPct(c.engagement_rate)}</span>
                    <span>Saves {fmtNumber(c.saves)}</span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

// ============================================================
// Performance section
// ============================================================
const PerformanceSection = ({ summary, trend, fullAccess, loading, token }) => {
  // Dropdown at the KPI card header — filters which analytics section
  // renders below. Default 'all' preserves the existing "show
  // everything" behaviour so opening the tab doesn't lose context.
  const [perfFilter, setPerfFilter] = useState('all');
  if (!summary) return loading ? <Skeleton className="h-48" /> : null;
  const pf = summary.performance || {};
  const ql = summary.quality || {};
  return (
    <>
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle className="text-base">Performance KPIs</CardTitle>
          <Select value={perfFilter} onValueChange={setPerfFilter}>
            <SelectTrigger className="h-8 w-[180px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All performance</SelectItem>
              <SelectItem value="ads">Ads performance</SelectItem>
              <SelectItem value="leads">Leads performance</SelectItem>
              <SelectItem value="sources">Source performance</SelectItem>
              <SelectItem value="campaigns">Campaign performance</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
          <KpiTile label="Total Leads" value={fmtNumber(pf.total_leads)} icon={Users} tone="violet" />
          <KpiTile label="Qualified" value={fmtNumber(pf.qualified)} icon={CheckCircle2} tone="blue" />
          <KpiTile label="Meetings" value={fmtNumber(pf.meetings)} icon={Activity} tone="blue" />
          {/* Site Visits + Bookings tiles removed per request — those
              metrics live in the dedicated Bookings / Site Visits pages
              and were duplicating the funnel info shown two rows below. */}
          <KpiTile label="Closed Sales" value={fmtNumber(pf.closed_sales)} icon={CheckCircle2} tone="emerald" />
          <KpiTile label="Lead → Qual" value={fmtPct(pf.lead_to_qualified_pct)} icon={TrendingUp} tone="blue" />
          <KpiTile label="Lead → Book" value={fmtPct(pf.lead_to_booking_pct)} icon={TrendingUp} tone="emerald" />
          {fullAccess && <KpiTile label="Revenue" value={fmtAED(pf.revenue)} icon={DollarSign} tone="emerald" />}
          {fullAccess && <KpiTile label="Spend" value={fmtAED(pf.spend)} icon={DollarSign} tone="rose" />}
          {fullAccess && <KpiTile label="CPL" value={fmtAED(pf.cpl)} icon={DollarSign} tone="amber" />}
          {fullAccess && <KpiTile label="CAC" value={fmtAED(pf.cac)} icon={DollarSign} tone="rose" />}
          {fullAccess && <KpiTile label="ROAS" value={pf.roas ? `${pf.roas}x` : '—'} icon={TrendingUp} tone="emerald" />}
          <KpiTile label="Avg Lead Score" value={fmtNumber(ql.avg_lead_score)} icon={Activity} tone="violet" />
          <KpiTile label="Avg Close Prob" value={fmtPct(ql.avg_close_probability)} icon={Activity} tone="violet" />
        </CardContent>
      </Card>

      {/* Source Analytics — AI insights + per-source/campaign/ad tables
          for the last 30 days. Same component as the Source Analytics
          top-level tab; embedded here so managers watching the
          Performance view see attribution alongside the KPIs. The
          filter comes from the dropdown in the KPI card header above. */}
      <SourceAnalyticsTab token={token} filter={perfFilter} />

      {/* Daily trend sparkline-style table */}
      {(trend.days || []).length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Daily lead inflow + conversion</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              {trend.days.slice(-14).map((d) => {
                const max = Math.max(...trend.days.map((x) => x.leads || 0), 1);
                const pct = ((d.leads || 0) / max) * 100;
                return (
                  <div key={d.date} className="flex items-center gap-2 text-xs">
                    <span className="w-24 text-slate-500">{d.date}</span>
                    <div className="flex-1 h-5 bg-slate-100 dark:bg-slate-800 rounded overflow-hidden relative">
                      <div className="h-full bg-violet-500/70" style={{ width: `${pct}%` }} />
                      <span className="absolute inset-0 px-2 flex items-center text-[10px] font-medium">
                        {d.leads} leads
                      </span>
                    </div>
                    <span className="w-24 text-right text-slate-500">
                      Qual {fmtPct(d.qual_pct)} · Close {fmtPct(d.close_pct)}
                    </span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
};

// ============================================================
// Funnel section (8-stage)
// ============================================================
const FunnelSection = ({ funnel, loading }) => {
  if (loading && funnel.length === 0) return <Skeleton className="h-64" />;
  if (funnel.length === 0) return <p className="text-sm text-slate-500">No funnel data.</p>;
  const max = Math.max(...funnel.map((f) => f.count || 0), 1);
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">8-stage lead funnel</CardTitle>
        <CardDescription>Drop-off % between adjacent stages.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {funnel.map((row) => {
            const pct = ((row.count || 0) / max) * 100;
            return (
              <div key={row.stage} className="flex items-center gap-3 text-sm">
                <div className="w-44">{row.label}</div>
                <div className="flex-1 h-7 bg-slate-100 dark:bg-slate-800 rounded overflow-hidden relative">
                  <div className="h-full bg-violet-500/80" style={{ width: `${pct}%` }} />
                  <div className="absolute inset-0 px-2 flex items-center text-xs font-medium">
                    {fmtNumber(row.count)}
                  </div>
                </div>
                <div className="w-44 text-xs text-right text-slate-500">
                  {row.conv_from_prev_pct == null
                    ? '—'
                    : `${row.conv_from_prev_pct}% from prev · ${row.drop_off_pct}% drop-off`}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================================
// Campaigns section
// ============================================================
const CampaignsSection = ({ campaigns, platforms, fullAccess, loading, token }) => {
  if (loading && (campaigns.top || []).length === 0) return <Skeleton className="h-48" />;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <CampaignTable title="Top campaigns" rows={campaigns.top || []} fullAccess={fullAccess} />
        <CampaignTable title="Bottom campaigns (review)" rows={campaigns.bottom || []} fullAccess={fullAccess} highlight />
      </div>

      {/* Campaign hygiene — Alerts (only when non-empty) + My Active
          Campaigns. Same widgets that appear on My Dashboard's Overview
          tab, embedded here so campaign managers have all the campaign
          context in one place instead of jumping pages. */}
      <CampaignAlertsAndActive token={token} />
    </div>
  );
};

const CampaignTable = ({ title, rows, fullAccess, highlight = false }) => (
  <Card>
    <CardHeader className="pb-2">
      <CardTitle className={`text-base ${highlight ? 'text-rose-700' : ''}`}>{title}</CardTitle>
    </CardHeader>
    <CardContent>
      {rows.length === 0 ? (
        <p className="text-sm text-slate-500">No campaigns in this window.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-slate-500 border-b">
            <tr>
              <th className="text-left py-2 px-2">Campaign</th>
              <th className="text-right py-2 px-2">Leads</th>
              <th className="text-right py-2 px-2">Won</th>
              <th className="text-right py-2 px-2">Conv %</th>
              {fullAccess && <th className="text-right py-2 px-2">Spend</th>}
              {fullAccess && <th className="text-right py-2 px-2">CPL</th>}
              {fullAccess && <th className="text-right py-2 px-2">ROAS</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={(r.campaign_id || '') + (r.campaign_name || '')} className="border-b border-slate-100 dark:border-slate-800">
                <td className="py-2 px-2">
                  <div className="font-medium">{r.campaign_name}</div>
                  {r.platform && <div className="text-[10px] text-slate-400">{PLATFORM_LABELS[r.platform] || r.platform}</div>}
                </td>
                <td className="text-right py-2 px-2 font-mono">{fmtNumber(r.leads)}</td>
                <td className="text-right py-2 px-2 font-mono">{fmtNumber(r.closed_won)}</td>
                <td className="text-right py-2 px-2 font-mono">{fmtPct(r.conv_pct)}</td>
                {fullAccess && <td className="text-right py-2 px-2 font-mono">{fmtAED(r.spend)}</td>}
                {fullAccess && <td className="text-right py-2 px-2 font-mono">{fmtAED(r.cpl)}</td>}
                {fullAccess && <td className="text-right py-2 px-2 font-mono">{r.roas ? `${r.roas}x` : '—'}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </CardContent>
  </Card>
);

// ============================================================
// Sources section
// ============================================================
const SourcesSection = ({ sources, loading }) => {
  if (loading && sources.length === 0) return <Skeleton className="h-48" />;
  if (sources.length === 0) return <p className="text-sm text-slate-500">No source data.</p>;
  const max = Math.max(...sources.map((s) => s.leads || 0), 1);
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Source attribution</CardTitle>
        <CardDescription>Leads + conversion per source.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-1.5">
          {sources.map((s) => {
            const pct = ((s.leads || 0) / max) * 100;
            return (
              <div key={s.source} className="flex items-center gap-3 text-sm">
                <div className="w-40 truncate">{s.source}</div>
                <div className="flex-1 h-6 bg-slate-100 dark:bg-slate-800 rounded overflow-hidden relative">
                  <div className="h-full bg-blue-500/70" style={{ width: `${pct}%` }} />
                  <div className="absolute inset-0 px-2 flex items-center text-xs">
                    {fmtNumber(s.leads)} leads · {fmtNumber(s.closed_won)} won
                  </div>
                </div>
                <div className="w-32 text-xs text-right text-slate-500">
                  Qual {fmtPct(s.qual_pct)} · Close {fmtPct(s.close_pct)}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
